import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, SearchInput, FormField, ProgressBar, Pagination } from '../components/common/UI';
import { PROJECT_STATUS, PROJECT_TYPES } from '../utils/statusHelpers'
import { toShamsi, formatCurrency, daysUntil, todayShamsi } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import EmployeeModal from '../components/common/EmployeeModal';
import { MdAdd, MdEdit, MdDelete, MdVisibility, MdPrint, MdReceipt } from 'react-icons/md';
import DatePicker from 'react-datepicker2';
import moment from 'moment-jalaali';
import { FaChartLine, FaMoneyBillWave } from 'react-icons/fa';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

const initialForm = { name: '', type: 'Wedding', client: '', eventDate: '', deliveryDate: '', status: 'Planned', package: 'معمولی', totalAmount: '', paidAmount: '0', location: '', notes: '', assignedTeam: [] };

export default function Projects() {
  const { isManager } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [invoiceItem, setInvoiceItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [clientForm, setClientForm] = useState({ name: '', phone: '', email: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [teamWithSalary, setTeamWithSalary] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [otherEventFields, setOtherEventFields] = useState({ eventType: '', guestCount: '' });
  const [mainCategory, setMainCategory] = useState('');
  const [projectSource, setProjectSource] = useState('');
  const [posterFields, setPosterFields] = useState({ width: '', height: '', format: '', quantity: '' });
  const [commercialFields, setCommercialFields] = useState({ duration: '', target: '', location: '' });
  const [corporateFields, setCorporateFields] = useState({ companyName: '', videoType: '', hasInterview: false });
  const [documentaryFields, setDocumentaryFields] = useState({ duration: '', topic: '', interviewCount: '' });
  const [weddingFields, setWeddingFields] = useState({ brideName: '', groomName: '', ceremonyType: '' });
  const [shamsiEventDate, setShamsiEventDate] = useState('');
  const [selectedLeader, setSelectedLeader] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [defaultLeaders, setDefaultLeaders] = useState([
    { employeeId: '', name: 'ادیب', role: 'Project Manager', salary: 0 },
    { employeeId: '', name: 'جعفری', role: 'Team Leader', salary: 0 }
  ]);
  const [rentalEquipment, setRentalEquipment] = useState([]);

const addRentalItem = () => {
  setRentalEquipment([...rentalEquipment, { name: '', price: 0 }]);
};

const updateRentalItem = (index, field, value) => {
  const newItems = [...rentalEquipment];
  newItems[index][field] = value;
  setRentalEquipment(newItems);
};

const removeRentalItem = (index) => {
  const newItems = rentalEquipment.filter((_, i) => i !== index);
  setRentalEquipment(newItems);
};

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('type', filterType);
      const [projRes, clientRes, empRes] = await Promise.all([
        API.get(`/projects?${params}`),
        API.get('/projects/clients/all'),
        API.get('/employees'),
      ]);
      setProjects(projRes.data);
      setClients(clientRes.data);
      setEmployees(empRes.data);
    } finally { setLoading(false); }
  }, [filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadDefaultLeaders = async () => {
      try {
        const { data } = await API.get('/employees');
        const projectManager = data.find(e => e.role === 'Project Manager' || e.role === 'مدیر پروژه');
        const teamLeader = data.find(e => e.role === 'Team Leader' || e.role === 'سرپرست');
        
        if (projectManager) {
          setDefaultLeaders(prev => {
            const newLeaders = [...prev];
            newLeaders[0].employeeId = projectManager._id;
            newLeaders[0].name = projectManager.name;
            return newLeaders;
          });
        }
        if (teamLeader) {
          setDefaultLeaders(prev => {
            const newLeaders = [...prev];
            newLeaders[1].employeeId = teamLeader._id;
            newLeaders[1].name = teamLeader.name;
            return newLeaders;
          });
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadDefaultLeaders();
  }, []);

  const filtered = projects.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.client?.name?.includes(search)
  );

  // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, filterStatus, filterType]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetCategoryFields = () => {
    setPosterFields({ width: '', height: '', format: '', quantity: '' });
    setCommercialFields({ duration: '', target: '', location: '' });
    setCorporateFields({ companyName: '', videoType: '', hasInterview: false });
    setDocumentaryFields({ duration: '', topic: '', interviewCount: '' });
    setWeddingFields({ brideName: '', groomName: '', ceremonyType: '' });
    setOtherEventFields({ eventType: '', guestCount: '' });
  };

  const openAdd = () => { 
    setForm(initialForm); 
    setEditItem(null); 
    setTeamWithSalary([]); 
    setMainCategory('');
    setProjectSource('');
    resetCategoryFields();
    setShowModal(true); 
  };
  
  const openEdit = (p) => {
    setForm({
      name: p.name,
      type: p.type,
      client: p.client?._id || '',
      eventDate: p.eventDate?.split('T')[0] || '',
      deliveryDate: p.deliveryDate?.split('T')[0] || '',
      status: p.status,
      package: p.package,
      totalAmount: p.totalAmount,
      paidAmount: p.paidAmount,
      location: p.location || '',
      notes: p.notes || '',
      assignedTeam: p.assignedTeam?.map(e => e._id) || []
    });
    
    // تبدیل type قدیمی به mainCategory جدید
    const typeToCategory = {
      'Wedding': 'wedding',
      'Commercial': 'commercial', 
      'Documentary': 'documentary'
    };
    
    setMainCategory(p.mainCategory || typeToCategory[p.type] || '');
    setProjectSource(p.projectSource || '');
    
    const fields = p.categoryFields || {};
    
    setPosterFields({
      width: fields.width || '',
      height: fields.height || '',
      format: fields.format || '',
      quantity: fields.quantity || ''
    });
    
    setCommercialFields({
      duration: fields.duration || '',
      target: fields.target || '',
      location: fields.location || ''
    });
  
    setOtherEventFields({
      eventType: fields.eventType || '',
      guestCount: fields.guestCount || ''
    });
    
    setCorporateFields({
      companyName: fields.companyName || '',
      videoType: fields.videoType || '',
      hasInterview: fields.hasInterview || false
    });
    
    setDocumentaryFields({
      duration: fields.duration || '',
      topic: fields.topic || '',
      interviewCount: fields.interviewCount || ''
    });
    
    setWeddingFields({
      brideName: fields.brideName || '',
      groomName: fields.groomName || '',
      ceremonyType: fields.ceremonyType || ''
    });
  
    // پیدا کردن مدیر پروژه و سرپرست تیم
    const projectLeader = p.assignedTeamWithSalary?.find(t => t.roles?.includes('مدیر پروژه'));
    const teamSupervisor = p.assignedTeamWithSalary?.find(t => t.roles?.includes('سرپرست تیم'));
    
    setSelectedLeader(projectLeader?.employeeId?._id || projectLeader?.employeeId || '');
    setSelectedSupervisor(teamSupervisor?.employeeId?._id || teamSupervisor?.employeeId || '');
    
    // بقیه تیم (بدون مدیر و سرپرست)
    const otherTeam = p.assignedTeamWithSalary?.filter(t => 
      !t.roles?.includes('مدیر پروژه') && !t.roles?.includes('سرپرست تیم')
    ) || [];
    setTeamWithSalary(otherTeam.map(t => ({
      employeeId: t.employeeId,
      salary: t.salary,
      roles: t.roles || []
    })));
    
    setEditItem(p);
    setShowModal(true);
  };

  const handleProjectPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0) {
      return toast.error('مبلغ را وارد کنید');
    }
    const newPaidAmount = (viewItem.paidAmount || 0) + Number(paymentAmount);
    
    try {
      await API.put(`/projects/${viewItem._id}`, {
        paidAmount: newPaidAmount
      });
      toast.success('پرداخت ثبت شد');
      setShowPaymentModal(false);
      setPaymentAmount('');
      load(); // رفرش لیست
      setViewItem(null); // بستن مودال جزئیات
    } catch (err) {
      toast.error('خطا در پرداخت');
    }
  };

  const getCategoryFieldsData = () => {
    switch(mainCategory) {
      case 'poster': return posterFields;
      case 'commercial': return commercialFields;
      case 'corporate': return corporateFields;
      case 'documentary': return documentaryFields;
      case 'wedding': return weddingFields;
      case 'otherEvent': return otherEventFields;
      default: return {};
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.client || !form.eventDate || !form.deliveryDate || !form.totalAmount) {
      return toast.error('لطفاً تمام فیلدهای ضروری را پر کنید');
    }
  
    const teamList = [];

    if (selectedLeader) {
      if (selectedLeader === 'ادیب') {
        teamList.push({ name: 'ادیب', role: 'مدیر پروژه', salary: 0 });
      } else if (selectedLeader === 'جعفری') {
        teamList.push({ name: 'جعفری', role: 'سرپرست تیم', salary: 0 });
      } else if (selectedLeader === 'نجیب') {
        teamList.push({ name: 'نجیب', role: 'سرپرست تیم', salary: 0 });
      } else if (selectedLeader === 'ادیب,جعفری') {
        teamList.push({ name: 'ادیب', role: 'مدیر پروژه', salary: 0 });
        teamList.push({ name: 'جعفری', role: 'سرپرست تیم', salary: 0 });
      }
    }
    
    const otherTeam = teamWithSalary.map(t => ({
      employeeId: t.employeeId,
      salary: t.salary,
      roles: t.roles || []
    }));
    
    const assignedTeamWithSalary = [...teamList, ...otherTeam];
    
    setSaving(true);
    try {
      const dataToSend = {
        ...form,
        mainCategory,
        projectSource,
        categoryFields: getCategoryFieldsData(),
        assignedTeamWithSalary,
        rentalEquipment: rentalEquipment
      };
      
      if (editItem) {
        await API.put(`/projects/${editItem._id}`, dataToSend);
        toast.success('پروژه بروزرسانی شد');
      } else {
        await API.post('/projects', dataToSend);
        toast.success('پروژه اضافه شد');
      }
      setShowModal(false);
      load();
    } catch (err) { 
      toast.error(err.response?.data?.message || 'خطا'); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/projects/${deleteId}`);
      toast.success('پروژه حذف شد');
      setDeleteId(null);
      load();
    } catch { toast.error('خطا در حذف'); }
  };

  const saveClient = async () => {
    if (!clientForm.name || !clientForm.phone) return toast.error('نام و شماره تلفن ضروری است');
    try {
      const { data } = await API.post('/projects/clients/create', clientForm);
      setClients(prev => [...prev, data]);
      setForm(f => ({ ...f, client: data._id }));
      setShowClientModal(false);
      setClientForm({ name: '', phone: '', email: '', address: '' });
      toast.success('مشتری اضافه شد');
    } catch { toast.error('خطا'); }
  };

  const renderCategoryFields = () => {
    switch(mainCategory) {
      case 'poster':
        return (
          <>
            <FormField label="طول (سانتی‌متر)" required>
              <input type="number" className="form-input" value={posterFields.width} onChange={e => setPosterFields({...posterFields, width: e.target.value})} placeholder="مثال: 50" />
            </FormField>
            <FormField label="عرض (سانتی‌متر)" required>
              <input type="number" className="form-input" value={posterFields.height} onChange={e => setPosterFields({...posterFields, height: e.target.value})} placeholder="مثال: 70" />
            </FormField>
            <FormField label="فرمت فایل">
              <select className="form-input" value={posterFields.format} onChange={e => setPosterFields({...posterFields, format: e.target.value})}>
                <option value="">انتخاب کنید</option>
                <option value="PSD">PSD</option>
                <option value="AI">AI</option>
                <option value="CDR">CDR</option>
                <option value="PDF">PDF</option>
                <option value="JPG">JPG</option>
              </select>
            </FormField>
            <FormField label="تعداد">
              <input type="number" className="form-input" value={posterFields.quantity} onChange={e => setPosterFields({...posterFields, quantity: e.target.value})} placeholder="مثال: 1" />
            </FormField>
          </>
        );
        case 'otherEvent':
  return (
    <>
      <FormField label="نوع مراسم" required>
        <select className="form-input" value={otherEventFields.eventType} onChange={e => setOtherEventFields({...otherEventFields, eventType: e.target.value})}>
          <option value="">انتخاب کنید</option>
          <option value="تولد">تولد</option>
          <option value="سالگرد">سالگرد</option>
          <option value="جشن">جشن</option>
          <option value="افتتاحیه">افتتاحیه</option>
          <option value="سایر">سایر</option>
        </select>
      </FormField>
      <FormField label="تعداد مهمانان">
        <input type="number" className="form-input" value={otherEventFields.guestCount} onChange={e => setOtherEventFields({...otherEventFields, guestCount: e.target.value})} placeholder="تعداد مهمانان" />
      </FormField>
    </>
  );
      case 'commercial':
        return (
          <>
            <FormField label="مدت ویدیو (ثانیه)" required>
              <input type="number" className="form-input" value={commercialFields.duration} onChange={e => setCommercialFields({...commercialFields, duration: e.target.value})} placeholder="مثال: 30" />
            </FormField>
            <FormField label="مخاطب هدف">
              <input className="form-input" value={commercialFields.target} onChange={e => setCommercialFields({...commercialFields, target: e.target.value})} placeholder="مثال: جوانان ۱۸-۳۰ سال" />
            </FormField>
            <FormField label="لوکیشن فیلمبرداری">
              <input className="form-input" value={commercialFields.location} onChange={e => setCommercialFields({...commercialFields, location: e.target.value})} placeholder="مثال: استودیو، خارجی" />
            </FormField>
          </>
        );
      case 'corporate':
        return (
          <>
            <FormField label="نام شرکت" required>
              <input className="form-input" value={corporateFields.companyName} onChange={e => setCorporateFields({...corporateFields, companyName: e.target.value})} placeholder="نام شرکت" />
            </FormField>
            <FormField label="نوع ویدیو">
              <select className="form-input" value={corporateFields.videoType} onChange={e => setCorporateFields({...corporateFields, videoType: e.target.value})}>
                <option value="">انتخاب کنید</option>
                <option value="معرفی شرکت">معرفی شرکت</option>
                <option value="معرفی محصول">معرفی محصول</option>
                <option value="گزارش عملکرد">گزارش عملکرد</option>
                <option value="آموزشی">آموزشی</option>
              </select>
            </FormField>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={corporateFields.hasInterview} onChange={e => setCorporateFields({...corporateFields, hasInterview: e.target.checked})} className="w-4 h-4 rounded" />
                <span className="text-gray-800 text-sm">دارای مصاحبه</span>
              </label>
            </div>
          </>
        );
      case 'documentary':
        return (
          <>
            <FormField label="مدت زمان (دقیقه)">
              <input type="number" className="form-input" value={documentaryFields.duration} onChange={e => setDocumentaryFields({...documentaryFields, duration: e.target.value})} placeholder="مثال: 45" />
            </FormField>
            <FormField label="موضوع مستند">
              <input className="form-input" value={documentaryFields.topic} onChange={e => setDocumentaryFields({...documentaryFields, topic: e.target.value})} placeholder="موضوع مستند" />
            </FormField>
            <FormField label="تعداد مصاحبه شونده‌ها">
              <input type="number" className="form-input" value={documentaryFields.interviewCount} onChange={e => setDocumentaryFields({...documentaryFields, interviewCount: e.target.value})} placeholder="مثال: 10" />
            </FormField>
          </>
        );
      case 'wedding':
        return (
          <>
            <FormField label="نام عروس">
              <input className="form-input" value={weddingFields.brideName} onChange={e => setWeddingFields({...weddingFields, brideName: e.target.value})} placeholder="نام عروس" />
            </FormField>
            <FormField label="نام داماد">
              <input className="form-input" value={weddingFields.groomName} onChange={e => setWeddingFields({...weddingFields, groomName: e.target.value})} placeholder="نام داماد" />
            </FormField>
            <FormField label="نوع مراسم">
              <select className="form-input" value={weddingFields.ceremonyType} onChange={e => setWeddingFields({...weddingFields, ceremonyType: e.target.value})}>
                <option value="">انتخاب کنید</option>
                <option value="عروسی">عروسی</option>
                <option value="نامزدی">نامزدی</option>
                <option value="عقد">عقد</option>
                <option value="حنا">حنا</option>
              </select>
            </FormField>
          </>
        );
      default: return null;
    }
  };

  const renderViewCategoryFields = () => {
    if (!viewItem?.categoryFields || !viewItem?.mainCategory) return null;
    
    const fields = viewItem.categoryFields;
    const category = viewItem.mainCategory;
    
    switch(category) {
      case 'poster':
        return (
          <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 rounded-lg">
            <div><span className="text-gray-600">طول:</span> <span className="text-gray-800">{fields.width || '—'} سانتی‌متر</span></div>
            <div><span className="text-gray-600">عرض:</span> <span className="text-gray-800">{fields.height || '—'} سانتی‌متر</span></div>
            <div><span className="text-gray-600">فرمت فایل:</span> <span className="text-gray-800">{fields.format || '—'}</span></div>
            <div><span className="text-gray-600">تعداد:</span> <span className="text-gray-800">{fields.quantity || '—'}</span></div>
          </div>
        );
        
      case 'commercial':
        return (
          <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 rounded-lg">
            <div><span className="text-gray-600">مدت ویدیو:</span> <span className="text-gray-800">{fields.duration || '—'} ثانیه</span></div>
            <div><span className="text-gray-600">مخاطب هدف:</span> <span className="text-gray-800">{fields.target || '—'}</span></div>
            <div className="col-span-2"><span className="text-gray-600">لوکیشن فیلمبرداری:</span> <span className="text-gray-800">{fields.location || '—'}</span></div>
          </div>
        );

        case 'otherEvent':
          return (
            <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-600">نوع مراسم:</span> <span className="text-gray-800">{fields.eventType || '—'}</span></div>
              <div><span className="text-gray-600">تعداد مهمانان:</span> <span className="text-gray-800">{fields.guestCount || '—'}</span></div>
            </div>
          );
        
      case 'corporate':
        return (
          <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 rounded-lg">
            <div><span className="text-gray-600">نام شرکت:</span> <span className="text-gray-800">{fields.companyName || '—'}</span></div>
            <div><span className="text-gray-600">نوع ویدیو:</span> <span className="text-gray-800">{fields.videoType || '—'}</span></div>
            <div><span className="text-gray-600">دارای مصاحبه:</span> <span className="text-gray-800">{fields.hasInterview ? 'بله' : 'خیر'}</span></div>
          </div>
        );
        
      case 'documentary':
        return (
          <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 rounded-lg">
            <div><span className="text-gray-600">مدت زمان:</span> <span className="text-gray-800">{fields.duration || '—'} دقیقه</span></div>
            <div><span className="text-gray-600">موضوع مستند:</span> <span className="text-gray-800">{fields.topic || '—'}</span></div>
            <div><span className="text-gray-600">تعداد مصاحبه شونده‌ها:</span> <span className="text-gray-800">{fields.interviewCount || '—'}</span></div>
          </div>
        );
        
      case 'wedding':
        return (
          <div className="grid grid-cols-2 gap-4 mt-2 p-3 bg-gray-50 rounded-lg">
            <div><span className="text-gray-600">نام عروس:</span> <span className="text-gray-800">{fields.brideName || '—'}</span></div>
            <div><span className="text-gray-600">نام داماد:</span> <span className="text-gray-800">{fields.groomName || '—'}</span></div>
            <div><span className="text-gray-600">نوع مراسم:</span> <span className="text-gray-800">{fields.ceremonyType || '—'}</span></div>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="پروژه‌ ها"
        subtitle={`${filtered.length} پروژه`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="پروژه جدید" />}
      />

      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1"><SearchInput value={search} onChange={setSearch} placeholder="جستجوی پروژه یا مشتری..." /></div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input sm:w-44">
            <option value="">همه وضعیت‌ها</option>
            {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="form-input sm:w-40">
            <option value="">همه انواع</option>
            {Object.entries(PROJECT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="📁" title="پروژه‌ای یافت نشد" subtitle="اولین پروژه خود را اضافه کنید" action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="پروژه جدید" />} />
      ) : (
        <>
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>نام پروژه</th><th>نوع</th><th>مشتری</th><th>تاریخ رویداد</th>
                    <th>تاریخ تحویل</th><th>وضعیت</th><th>مبلغ کل</th><th>باقیات</th><th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(p => {
                    const remaining = p.totalAmount - p.paidAmount;
                    const days = daysUntil(p.deliveryDate);
                    return (
                      <tr key={p._id}>
                        <td><div className="font-semibold text-gray-800">{p.name}</div>{p.location && <div className="text-xs text-gray-600">{p.location}</div>}</td>
                        <td className="text-gray-600">
                        {p.mainCategory === 'wedding' ? 'مراسم عروسی' : 
                        p.mainCategory === 'funeral' ? 'مراسم فاتحه' :
                        p.mainCategory === 'poster' ? 'دیزاین پوستر' :
                        p.mainCategory === 'commercial' ? 'تبلیغاتی' :
                        p.mainCategory === 'corporate' ? 'شرکتی' :
                        p.mainCategory === 'documentary' ? 'مستند' : PROJECT_TYPES[p.type] || p.type}
                      </td>
                        <td><div className="text-gray-800">{p.client?.name || '---'}</div>{p.client?.phone && <div className="text-xs text-gray-600">{p.client.phone}</div>}</td>
                        <td className="text-gray-600 text-sm">{toShamsi(p.eventDate)}</td>
                        <td>
                          <div className="text-gray-600 text-sm">{toShamsi(p.deliveryDate)}</div>
                          {p.status !== 'Delivered' && (
                            <div className={`text-xs mt-0.5 ${days < 0 ? 'text-red-600' : days <= 3 ? 'text-yellow-600' : 'text-gray-600'}`}>
                              {days < 0 ? `${Math.abs(days)} روز تأخیر` : days === 0 ? 'امروز!' : `${days} روز مانده`}
                            </div>
                          )}
                        </td>
                        <td><Badge statusConfig={PROJECT_STATUS[p.status]} /></td>
                        <td className="text-green-600 font-medium">{formatCurrency(p.totalAmount)}</td>
                        <td className={remaining > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>{formatCurrency(remaining)}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setViewItem(p)} className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-all"><MdVisibility size={15} /></button>
                            <button onClick={() => setInvoiceItem(p)} className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-all"><MdReceipt size={15} /></button>
                            {isManager && <button onClick={() => openEdit(p)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                            {isManager && <button onClick={() => setDeleteId(p._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {paged.map(p => {
              const remaining = p.totalAmount - p.paidAmount;
              const paidPct = p.totalAmount ? (p.paidAmount / p.totalAmount) * 100 : 0;
              return (
                <div key={p._id} className="mobile-card">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-semibold text-gray-800">{p.name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{p.client?.name} • {PROJECT_TYPES[p.type]}</div>
                    </div>
                    <Badge statusConfig={PROJECT_STATUS[p.status]} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div><span className="text-gray-600 text-xs">رویداد:</span><br /><span className="text-gray-600">{toShamsi(p.eventDate)}</span></div>
                    <div><span className="text-gray-600 text-xs">تحویل:</span><br /><span className="text-gray-600">{toShamsi(p.deliveryDate)}</span></div>
                    <div><span className="text-gray-600 text-xs">مبلغ کل:</span><br /><span className="text-green-600 font-medium">{formatCurrency(p.totalAmount)}</span></div>
                    <div><span className="text-gray-600 text-xs">باقیات:</span><br /><span className={remaining > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>{formatCurrency(remaining)}</span></div>
                  </div>
                  <ProgressBar value={paidPct} label="پرداخت شده" color={paidPct === 100 ? 'green' : 'blue'} />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setInvoiceItem(p)} className="flex-1 py-1.5 text-xs bg-green-100 hover:bg-green-200 text-green-600 rounded-lg transition-colors flex items-center justify-center gap-1"><MdReceipt size={14} /> فاکتور</button>
                    {isManager && (
                      <>
                        <button onClick={() => openEdit(p)} className="flex-1 py-1.5 text-xs bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-lg transition-colors">ویرایش</button>
                        <button onClick={() => setDeleteId(p._id)} className="flex-1 py-1.5 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors">حذف</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

<Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش پروژه' : 'پروژه جدید'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="نام پروژه" required>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="نام پروژه" />
          </FormField>
          <FormField label="از طرف">
            <input 
              type="text" 
              className="form-input" 
              value={projectSource} 
              onChange={e => setProjectSource(e.target.value)} 
              placeholder="مثال: دفتر، جعفری، ادیب..." 
            />
          </FormField>
          
          <FormField label="نوع پروژه" required>
            <select className="form-input" value={mainCategory} onChange={e => {
              setMainCategory(e.target.value);
              resetCategoryFields();
              // اگر مراسم خوشی انتخاب شد، تاریخ تحویل = تاریخ رویداد
              if (e.target.value === 'wedding') {
                const eventDate = form.eventDate;
                if (eventDate) {
                  setForm({ ...form, deliveryDate: eventDate });
                }
              }
            }}>
              <option value="">انتخاب کنید</option>
              <option value="wedding">مراسم خوشی</option>
              <option value="funeral">مراسم فاتحه</option>
              <option value="otherEvent">سایر مراسم</option>
              <option value="poster">دیزاین پوستر</option>
              <option value="commercial">تبلیغاتی</option>
              <option value="corporate">شرکتی</option>
              <option value="documentary">مستند</option>
            </select>
          </FormField>
          
          {renderCategoryFields()}
          
          <FormField label="مشتری" required>
            <div className="flex gap-2">
              <select className="form-input flex-1" value={form.client} onChange={e => setForm({ ...form, client: e.target.value })}>
                <option value="">انتخاب مشتری</option>
                {clients.map(c => <option key={c._id} value={c._id}>{c.name} - {c.phone}</option>)}
              </select>
              <button onClick={() => setShowClientModal(true)} className="px-3 py-2 bg-blue-100 hover:bg-blue-2000 text-blue-700 rounded-xl text-sm whitespace-nowrap transition-colors">جدید</button>
            </div>
          </FormField>
          
          <FormField label="تاریخ بوکینگ (رزرو)">
          <ShamsiDatePicker
            value={form.eventDate}
            onChange={(gregorianDate) => setForm({ ...form, eventDate: gregorianDate })}
            className="form-input w-full"
          />
          </FormField>
          
          <FormField label="تاریخ رویداد" required>
  <ShamsiDatePicker
    value={form.eventDate}
    onChange={(gregorianDate) => {
      setForm({ ...form, eventDate: gregorianDate });
      // اگر مراسم خوشی است، تاریخ تحویل = تاریخ رویداد
      if (mainCategory === 'wedding') {
        setForm(prev => ({ ...prev, deliveryDate: gregorianDate }));
      }
    }}
    className="form-input w-full"
    placeholder="انتخاب تاریخ"
  />
</FormField>
          
<FormField label="تاریخ تحویل" required>
  <ShamsiDatePicker
    value={form.deliveryDate}
    onChange={(gregorianDate) => setForm({ ...form, deliveryDate: gregorianDate })}
    className={mainCategory === 'wedding' ? "form-input bg-gray-50 cursor-not-allowed" : "form-input w-full"}
    placeholder="انتخاب تاریخ"
    readOnly={mainCategory === 'wedding'}
  />
  {mainCategory === 'wedding' && (
    <p className="text-xs text-blue-600 mt-1">⚠️ در مراسم خوشی، چیپ در همان روز رویداد تحویل داده میشود</p>
  )}
</FormField>
          
          <FormField label="وضعیت">
            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FormField>
          
          <FormField label="لوکیشن">
            <input className="form-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="لوکیشن رویداد" />
          </FormField>
          
          <FormField label="مبلغ کل (افغانی)" required>
            <input type="number" className="form-input" value={form.totalAmount} onChange={e => setForm({ ...form, totalAmount: e.target.value })} placeholder="0" />
          </FormField>
          
          <FormField label="مبلغ پرداخت شده (افغانی)">
            <input type="number" className="form-input" value={form.paidAmount} onChange={e => setForm({ ...form, paidAmount: e.target.value })} placeholder="0" />
          </FormField>
          
          <div className="sm:col-span-2">
          <FormField label="مدیر / سرپرست پروژه">
            <select 
              className="form-input" 
              value={selectedLeader} 
              onChange={e => setSelectedLeader(e.target.value)}
            >
              <option value="">انتخاب کنید</option>
              <option value="ادیب">ادیب (مدیر پروژه)</option>
              <option value="جعفری">جعفری (سرپرست تیم)</option>
              <option value="نجیب">نجیب (سرپرست تیم)</option>
              <option value="ادیب,جعفری">ادیب و جعفری (هر دو)</option>
            </select>
          </FormField>

  {/* بقیه تیم */}
  <FormField label="تیم assign شده با تعیین دستمزد و نقش">
    <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-xl p-3">
      {employees.filter(e => e.status !== 'Inactive' && e.role !== 'Project Manager' && e.role !== 'Team Leader').map(emp => {
        const existing = teamWithSalary.find(t => t.employeeId === emp._id);
        return (
          <div key={emp._id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
            <input type="checkbox" checked={!!existing} onChange={(e) => {
              if (e.target.checked) {
                setTeamWithSalary([...teamWithSalary, { employeeId: emp._id, salary: 0, roles: [] }]);
              } else {
                setTeamWithSalary(teamWithSalary.filter(t => t.employeeId !== emp._id));
              }
            }} className="w-4 h-4 rounded" />
            <div className="flex-1">
              <div className="text-gray-800 text-sm">{emp.name}</div>
              <div className="text-xs text-gray-500">{emp.role}</div>
            </div>
            {existing && (
              <div className="flex gap-2 items-center">
                <select 
                  multiple
                  value={existing.roles || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                    setTeamWithSalary(teamWithSalary.map(t => 
                      t.employeeId === emp._id ? { ...t, roles: selected } : t
                    ));
                  }}
                  className="w-32 form-input py-1 text-sm"
                >
                  <option value="ادیتور">ادیتور</option>
                  <option value="فیلمبردار">فیلمبردار</option>
                  <option value="عکاس">عکاس</option>
                  <option value="طراح">طراح</option>
                </select>
                <input 
                  type="number" 
                  placeholder="دستمزد" 
                  value={existing.salary || ''}
                  onChange={(e) => {
                    setTeamWithSalary(teamWithSalary.map(t =>
                      t.employeeId === emp._id ? { ...t, salary: Number(e.target.value) } : t
                    ));
                  }}
                  className="w-28 form-input py-1 text-sm" 
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
    <p className="text-xs text-gray-600 mt-1">✅ کارمند را انتخاب کنید، وظیفه و دستمزد او را مشخص کنید</p>
  </FormField>
  
  <button type="button" onClick={() => setShowEmployeeModal(true)} className="w-full mt-2 py-1.5 text-xs bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
    + کارمند جدید اضافه کن
  </button>
</div>

<div className="sm:col-span-2">
  <FormField label="وسایل کرایه‌ای">
    <div className="space-y-2">
      {rentalEquipment.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
          <input 
            type="text" 
            className="form-input" 
            style={{ flex: 3 }}
            placeholder="نام وسیله" 
            value={item.name} 
            onChange={(e) => updateRentalItem(idx, 'name', e.target.value)}
          />
          <input 
            type="number" 
            className="form-input" 
            style={{ flex: 1 }}
            placeholder="قیمت" 
            value={item.price || ''} 
            onChange={(e) => updateRentalItem(idx, 'price', Number(e.target.value))}
          />
          <button 
            type="button" 
            onClick={() => removeRentalItem(idx)} 
            className="p-1.5 text-red-600 hover:text-red-700 rounded-lg"
          >
            ✖
          </button>
        </div>
      ))}
      <button 
        type="button" 
        onClick={addRentalItem} 
        className="w-full mt-2 py-2 text-sm bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
      >
        + افزودن وسیله کرایه‌ای
      </button>
    </div>
    <p className="text-xs text-gray-600 mt-2">⚠️ قیمت وسایل کرایه‌ای از مبلغ کل پروژه کم خواهد شد</p>
  </FormField>
</div>
          
          <div className="sm:col-span-2">
            <FormField label="یادداشت">
              <textarea className="form-input h-20 resize-none" value={form.notes} onChange={e => {
                setForm({ ...form, notes: e.target.value });
                // اگر مراسم خوشی است و تاریخ تحویل تنظیم شد، کامنت خودکار اضافه کن
                if (mainCategory === 'wedding' && form.deliveryDate === form.eventDate) {
                  const weddingNote = "✅ محصول در همان روز رویداد تحویل داده شد.";
                  if (!form.notes?.includes(weddingNote)) {
                    setForm({ ...form, notes: (form.notes ? form.notes + "\n" : "") + weddingNote });
                  }
                }
              }} placeholder="یادداشت اختیاری..." />
            </FormField>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">انصراف</button>
          <ActionBtn onClick={handleSave} label={saving ? 'در حال ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
        </div>
      </Modal>
      <Modal isOpen={showClientModal} onClose={() => setShowClientModal(false)} title="مشتری جدید" size="sm">
        <div className="space-y-4">
          <FormField label="نام" required><input className="form-input" value={clientForm.name} onChange={e => setClientForm({ ...clientForm, name: e.target.value })} placeholder="نام مشتری" /></FormField>
          <FormField label="شماره تلفن" required><input className="form-input" value={clientForm.phone} onChange={e => setClientForm({ ...clientForm, phone: e.target.value })} placeholder="07xx-xxx-xxx" /></FormField>
          <FormField label="ایمیل"><input className="form-input" value={clientForm.email} onChange={e => setClientForm({ ...clientForm, email: e.target.value })} placeholder="email@example.com" /></FormField>
          <FormField label="آدرس"><input className="form-input" value={clientForm.address} onChange={e => setClientForm({ ...clientForm, address: e.target.value })} placeholder="آدرس مشتری" /></FormField>
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button onClick={() => setShowClientModal(false)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm hover:bg-gray-200 transition-colors">انصراف</button>
          <ActionBtn onClick={saveClient} label="ذخیره" />
        </div>
      </Modal>

      <EmployeeModal isOpen={showEmployeeModal} onClose={() => setShowEmployeeModal(false)} onSuccess={(newEmployee) => {
        if (newEmployee) {
          setTeamWithSalary(prev => [...prev, { employeeId: newEmployee._id, salary: 0 }]);
          API.get('/employees').then(res => setEmployees(res.data));
        }
      }} />

      {viewItem && (
        <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="جزئیات پروژه" size="lg">
          <div className="space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{viewItem.name}</h2>
                <div className="flex gap-2 mt-1">
                {viewItem.projectSource && <span className="text-xs text-gray-500">از طرف: {viewItem.projectSource}</span>}
                  {viewItem.location && <span className="text-xs text-gray-500">{viewItem.location}</span>}
                </div>
              </div>
              <Badge statusConfig={PROJECT_STATUS[viewItem.status]} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
              {[
                ['تاریخ بوکینگ', viewItem.bookingDate && toShamsi(viewItem.bookingDate)],
                ['مشتری', viewItem.client?.name],
                ['تلفن مشتری', viewItem.client?.phone],
                ['تاریخ رویداد', toShamsi(viewItem.eventDate)],
                ['تاریخ تحویل', toShamsi(viewItem.deliveryDate)],
                ['مبلغ کل', formatCurrency(viewItem.totalAmount)],
                ['پرداخت شده', formatCurrency(viewItem.paidAmount)],
                ['باقیات', formatCurrency(viewItem.totalAmount - viewItem.paidAmount)],
              ].map(([label, val]) => val && (
                <div key={label}>
                  <div className="text-xs text-gray-600 mb-0.5">{label}</div>
                  <div className="text-gray-800 text-sm font-medium">{val}</div>
                </div>
              ))}
            </div>

            {viewItem.mainCategory && (
              <div>
                <div className="text-xs text-gray-600 mb-2">اطلاعات اختصاصی</div>
                {renderViewCategoryFields()}
              </div>
            )}

{viewItem.assignedTeamWithSalary && viewItem.assignedTeamWithSalary.length > 0 && (
  <div>
    <div className="text-xs text-gray-600 mb-2">تیم اجرایی و دستمزد</div>
    <div className="space-y-2">
      {viewItem.assignedTeamWithSalary.map((emp, idx) => (
        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
          <div>
            <div className="text-gray-800 text-sm font-medium">
              {emp.employeeId?.name || emp.name || 'کارمند'}
            </div>
            <div className="text-xs text-gray-500">
              {emp.roles?.join('، ') || emp.role || emp.employeeId?.role || '—'}
            </div>
          </div>
          <div className="text-green-600 text-sm font-medium">
            {formatCurrency(emp.salary)}
          </div>
        </div>
      ))}
    </div>
  </div>
)}

{viewItem.rentalEquipment && viewItem.rentalEquipment.length > 0 && (
  <div>
    <div className="text-xs text-gray-600 mb-2">وسایل کرایه‌ای</div>
    <div className="space-y-2">
      {viewItem.rentalEquipment.map((item, idx) => (
        <div key={idx} className="flex justify-between bg-gray-50 rounded-lg p-2">
          <span className="text-gray-800">{item.name}</span>
          <span className="text-red-600">{formatCurrency(item.price)}</span>
        </div>
      ))}
    </div>
    <div className="flex justify-end mt-2 pt-2 border-t border-gray-200">
      <span className="text-xs text-gray-500">جمع هزینه کرایه:</span>
      <span className="text-sm text-red-600 font-medium mr-2">
        {formatCurrency(viewItem.rentalEquipment.reduce((sum, i) => sum + i.price, 0))}
      </span>
    </div>
  </div>
)}

{/* محاسبات مالی پروژه */}
<div className="border-t border-gray-200 pt-4 mt-2">
  <div className="text-xs text-gray-600 mb-3 flex items-center gap-2">
    <FaChartLine className="text-blue-600" />
    خلاصه مالی پروژه
  </div>
  
  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
    {/* درآمد */}
    <div className="flex justify-between items-center">
      <span className="text-gray-500 text-sm">مبلغ کل پروژه:</span>
      <span className="text-green-600 font-bold">{formatCurrency(viewItem.totalAmount)}</span>
    </div>
    
    {/* هزینه دستمزد تیم */}
    {viewItem.assignedTeamWithSalary && viewItem.assignedTeamWithSalary.length > 0 && (
      <div className="flex justify-between items-center">
        <span className="text-gray-500 text-sm">جمع دستمزد تیم:</span>
        <span className="text-red-600">
          - {formatCurrency(viewItem.assignedTeamWithSalary.reduce((sum, emp) => sum + (emp.salary || 0), 0))}
        </span>
      </div>
    )}
    
    {/* هزینه وسایل کرایه‌ای */}
    {viewItem.rentalEquipment && viewItem.rentalEquipment.length > 0 && (
      <div className="flex justify-between items-center">
        <span className="text-gray-500 text-sm">هزینه وسایل کرایه‌ای:</span>
        <span className="text-red-600">
          - {formatCurrency(viewItem.rentalEquipment.reduce((sum, i) => sum + (i.price || 0), 0))}
        </span>
      </div>
    )}
    
    {/* جمع هزینه‌ها */}
    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
      <span className="text-gray-500 text-sm">جمع هزینه‌ها:</span>
      <span className="text-red-600 font-bold">
        {formatCurrency(
          (viewItem.assignedTeamWithSalary?.reduce((sum, emp) => sum + (emp.salary || 0), 0) || 0) +
          (viewItem.rentalEquipment?.reduce((sum, i) => sum + (i.price || 0), 0) || 0)
        )}
      </span>
    </div>
    
    {/* سود خالص */}
    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
      <span className="text-gray-500 text-sm">سود خالص پروژه:</span>
      <span className={`font-bold text-lg ${(viewItem.totalAmount - 
        (viewItem.assignedTeamWithSalary?.reduce((sum, emp) => sum + (emp.salary || 0), 0) || 0) -
        (viewItem.rentalEquipment?.reduce((sum, i) => sum + (i.price || 0), 0) || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {formatCurrency(
          (viewItem.totalAmount || 0) - 
          (viewItem.assignedTeamWithSalary?.reduce((sum, emp) => sum + (emp.salary || 0), 0) || 0) -
          (viewItem.rentalEquipment?.reduce((sum, i) => sum + (i.price || 0), 0) || 0)
        )}
      </span>
    </div>
    
    {/* باقی مانده از مشتری */}
    {(viewItem.totalAmount - viewItem.paidAmount) > 0 && (
      <div className="flex justify-between items-center pt-2 border-t border-dashed border-yellow-200">
        <span className="text-gray-500 text-sm">باقی مانده از مشتری:</span>
        <span className="text-yellow-600 font-bold">{formatCurrency(viewItem.totalAmount - viewItem.paidAmount)}</span>
      </div>
    )}

    {/* دکمه پرداخت باقی‌مانده */}
    {(viewItem.totalAmount - viewItem.paidAmount) > 0 && (
      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => setShowPaymentModal(true)}
          className="w-full py-2.5 bg-green-600 hover:bg-green-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          <FaMoneyBillWave size={16} />
          پرداخت باقی‌مانده ({formatCurrency(viewItem.totalAmount - viewItem.paidAmount)})
        </button>
      </div>
    )}
  </div>
</div>

            {viewItem.notes && (
              <div>
                <div className="text-xs text-gray-600 mb-1">یادداشت</div>
                <p className="text-gray-600 text-sm bg-gray-50 rounded-xl p-3">{viewItem.notes}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <ActionBtn onClick={() => { setViewItem(null); setInvoiceItem(viewItem); }} icon={<MdReceipt size={16} />} label="تولید فاکتور" variant="success" />
              <button onClick={() => setViewItem(null)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">بستن</button>
            </div>
          </div>
        </Modal>
        
      )}

      {invoiceItem && <InvoiceModal project={invoiceItem} onClose={() => setInvoiceItem(null)} todayDate={todayShamsi()} />}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف پروژه" message="آیا مطمئن هستید؟" />
        {/* مودال پرداخت باقی‌مانده پروژه */}
{showPaymentModal && (
  <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="پرداخت باقی‌مانده" size="sm">
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-gray-800">پروژه: {viewItem.name}</div>
        <div className="text-red-600 text-2xl font-bold mt-2">
          {formatCurrency(viewItem.totalAmount - viewItem.paidAmount)}
        </div>
        <div className="text-gray-500 text-sm">باقی‌مانده قابل پرداخت</div>
      </div>
      <input 
        type="number" 
        className="form-input" 
        placeholder="مبلغ پرداختی" 
        value={paymentAmount} 
        onChange={e => setPaymentAmount(e.target.value)}
        max={viewItem.totalAmount - viewItem.paidAmount}
      />
      <div className="flex gap-3 justify-end">
        <button onClick={() => setShowPaymentModal(false)} className="px-4 py-2 bg-gray-100 rounded-lg">انصراف</button>
        <button onClick={handleProjectPayment} className="px-4 py-2 bg-green-600 rounded-lg">تأیید پرداخت</button>
      </div>
    </div>
  </Modal>
)}
    </div>
  );
}

function InvoiceModal({ project, onClose, todayDate }) {
  const remaining = project.totalAmount - project.paidAmount;

  const handlePrint = () => {
    const el = document.getElementById('invoice-print-area');
    const orig = document.body.innerHTML;
    document.body.innerHTML = el.innerHTML;
    window.print();
    document.body.innerHTML = orig;
    window.location.reload();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="فاکتور پروژه" size="lg">
  <div id="invoice-print-area">
    <div className="bg-white text-gray-900 rounded-xl p-8 font-sans" style={{ direction: 'rtl', fontFamily: 'Vazirmatn, Arial, sans-serif' }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-gray-200">
        <div>
          <div className="text-2xl font-bold text-blue-700">ویدا پرودکشن</div>
          <div className="text-gray-600 text-sm mt-1">Vida Production</div>
        </div>
        <div className="text-left">
          <div className="text-3xl font-bold text-gray-800">فاکتور</div>
          <div className="text-gray-600 text-sm mt-1">تاریخ: {todayDate}</div>
          <div className="text-gray-600 text-sm">شماره: INV-{project._id?.slice(-6)?.toUpperCase()}</div>
        </div>
      </div>

      {/* Client & Project Info */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">مشتری</div>
          <div className="text-lg font-bold text-gray-800">{project.client?.name || '---'}</div>
          {project.client?.phone && <div className="text-gray-600 text-sm">{project.client.phone}</div>}
          {project.client?.address && <div className="text-gray-600 text-sm">{project.client.address}</div>}
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">پروژه</div>
          <div className="text-lg font-bold text-gray-800">{project.name}</div>
          <div className="text-gray-600 text-sm">نوع: {
            project.mainCategory === 'wedding' ? 'مراسم عروسی' : 
            project.mainCategory === 'funeral' ? 'مراسم فاتحه' :
            project.mainCategory === 'poster' ? 'دیزاین پوستر' :
            project.mainCategory === 'commercial' ? 'تبلیغاتی' :
            project.mainCategory === 'corporate' ? 'شرکتی' :
            project.mainCategory === 'documentary' ? 'مستند' : PROJECT_TYPES[project.type] || project.type
          }</div>
          {project.projectSource && <div className="text-gray-600 text-sm">از طرف: {project.projectSource}</div>}
          {project.location && <div className="text-gray-600 text-sm">لوکیشن: {project.location}</div>}
          <div className="text-gray-600 text-sm">تاریخ بوکینگ: {toShamsi(project.bookingDate)}</div>
          <div className="text-gray-600 text-sm">تاریخ رویداد: {toShamsi(project.eventDate)}</div>
          <div className="text-gray-600 text-sm">تاریخ تحویل: {toShamsi(project.deliveryDate)}</div>
        </div>
      </div>

      {/* اطلاعات اختصاصی کتگوری */}
      {project.mainCategory && project.categoryFields && Object.keys(project.categoryFields).length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-3">
            جزئیات {project.mainCategory === 'wedding' ? 'مراسم عروسی' : 
              project.mainCategory === 'funeral' ? 'مراسم فاتحه' :
              project.mainCategory === 'poster' ? 'دیزاین پوستر' :
              project.mainCategory === 'commercial' ? 'تبلیغاتی' :
              project.mainCategory === 'corporate' ? 'شرکتی' : 'مستند'}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            {project.mainCategory === 'poster' && (
              <>
                <div><span className="text-gray-600">طول:</span> <span className="font-medium">{project.categoryFields.width || '—'} سانتی‌متر</span></div>
                <div><span className="text-gray-600">عرض:</span> <span className="font-medium">{project.categoryFields.height || '—'} سانتی‌متر</span></div>
                <div><span className="text-gray-600">فرمت:</span> <span className="font-medium">{project.categoryFields.format || '—'}</span></div>
                <div><span className="text-gray-600">تعداد:</span> <span className="font-medium">{project.categoryFields.quantity || '—'}</span></div>
              </>
            )}
            {project.mainCategory === 'commercial' && (
              <>
                <div><span className="text-gray-600">مدت ویدیو:</span> <span className="font-medium">{project.categoryFields.duration || '—'} ثانیه</span></div>
                <div><span className="text-gray-600">مخاطب هدف:</span> <span className="font-medium">{project.categoryFields.target || '—'}</span></div>
                <div className="col-span-2"><span className="text-gray-600">لوکیشن:</span> <span className="font-medium">{project.categoryFields.location || '—'}</span></div>
              </>
            )}
            {project.mainCategory === 'corporate' && (
              <>
                <div><span className="text-gray-600">نام شرکت:</span> <span className="font-medium">{project.categoryFields.companyName || '—'}</span></div>
                <div><span className="text-gray-600">نوع ویدیو:</span> <span className="font-medium">{project.categoryFields.videoType || '—'}</span></div>
                <div><span className="text-gray-600">دارای مصاحبه:</span> <span className="font-medium">{project.categoryFields.hasInterview ? 'بله' : 'خیر'}</span></div>
              </>
            )}
            {project.mainCategory === 'documentary' && (
              <>
                <div><span className="text-gray-600">مدت زمان:</span> <span className="font-medium">{project.categoryFields.duration || '—'} دقیقه</span></div>
                <div><span className="text-gray-600">موضوع:</span> <span className="font-medium">{project.categoryFields.topic || '—'}</span></div>
                <div><span className="text-gray-600">تعداد مصاحبه:</span> <span className="font-medium">{project.categoryFields.interviewCount || '—'}</span></div>
              </>
            )}
            {project.mainCategory === 'wedding' && (
              <>
                <div><span className="text-gray-600">نام عروس:</span> <span className="font-medium">{project.categoryFields.brideName || '—'}</span></div>
                <div><span className="text-gray-600">نام داماد:</span> <span className="font-medium">{project.categoryFields.groomName || '—'}</span></div>
                <div><span className="text-gray-600">نوع مراسم:</span> <span className="font-medium">{project.categoryFields.ceremonyType || '—'}</span></div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Services Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
        <thead>
          <tr style={{ backgroundColor: '#1e40af', color: 'white' }}>
            <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px' }}>خدمات</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '13px' }}>مبلغ</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
          <td style={{ padding: '12px 14px', fontSize: '14px' }}>
            {project.mainCategory === 'wedding' ? 'مراسم عروسی' : 
            project.mainCategory === 'funeral' ? 'مراسم فاتحه' :
            project.mainCategory === 'poster' ? 'دیزاین پوستر' :
            project.mainCategory === 'commercial' ? 'تبلیغاتی' :
            project.mainCategory === 'corporate' ? 'شرکتی' :
            project.mainCategory === 'documentary' ? 'مستند' : PROJECT_TYPES[project.type] || project.type}
          </td>
            <td style={{ padding: '12px 14px', textAlign: 'center', fontSize: '14px' }}>{project.package}</td>
            <td style={{ padding: '12px 14px', textAlign: 'left', fontSize: '14px', fontWeight: 'bold' }}>{formatCurrency(project.totalAmount)}</td>
          </tr>
          {project.notes && (
            <tr style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
              <td colSpan={3} style={{ padding: '10px 14px', fontSize: '13px', color: '#6b7280' }}>یادداشت: {project.notes}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ marginLeft: 'auto', width: '280px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
          <span style={{ color: '#6b7280' }}>مبلغ کل:</span>
          <span style={{ fontWeight: 'bold' }}>{formatCurrency(project.totalAmount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #e5e7eb', fontSize: '14px' }}>
          <span style={{ color: '#059669' }}>پرداخت شده:</span>
          <span style={{ color: '#059669', fontWeight: 'bold' }}>- {formatCurrency(project.paidAmount)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontSize: '16px', fontWeight: 'bold', color: remaining > 0 ? '#dc2626' : '#059669' }}>
          <span>باقیات:</span>
          <span>{formatCurrency(remaining)}</span>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
  <div className="text-center w-32">
    <div className="text-xs text-gray-500">مهر و امضاء شرکت</div>
    <div className="mt-6 border-t border-gray-300 pt-1 text-sm">...........................</div>
    <div className="text-[10px] text-gray-500">نام و امضاء</div>
  </div>
  <div className="text-center w-32">
    <div className="text-xs text-gray-500">امضاء مشتری</div>
    <div className="mt-6 border-t border-gray-300 pt-1 text-sm">...........................</div>
    <div className="text-[10px] text-gray-500">نام مشتری</div>
  </div>
  <div className="text-center w-32">
    <div className="text-xs text-gray-500">تاریخ</div>
    <div className="mt-6 text-sm">{todayDate}</div>
  </div>
</div>

      {/* Footer */}
      <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>
        <p>از انتخاب شما متشکریم • ویدا پروداکشن</p>
        <p style={{ marginTop: '4px' }}>این فاکتور توسط سیستم مدیریت ویدا پرودکشن تولید شده است</p>
      </div>
    </div>
  </div>

  <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-gray-200">
    <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">بستن</button>
    <ActionBtn onClick={handlePrint} icon={<MdPrint size={16} />} label="چاپ / ذخیره PDF" variant="success" />
  </div>
</Modal>


  );
}